/**
 * slideTransformTelemetry.ts — error reporting for the Slide Transform feature.
 *
 * Failures go through the shared telemetry pipeline (scrubbed), never
 * `console.*`, so nothing from a customer deck or a backend error body can leak
 * into browser logs. Mirrors the speakerNotesApi.ts helpers.
 */

import { logError, scrubMessage } from "../../../../../telemetry";

/** connectorSystemName tag for all Slide Transform telemetry. */
const SLIDE_TRANSFORM_CONNECTOR = "deckorate";

/**
 * The extra fields an Office.js failure carries beyond a plain Error.
 *
 * `OfficeExtension.Error` reports a terse `message` — often just the code, e.g.
 * "InvalidArgument" — while the actionable part sits in `debugInfo.errorLocation`,
 * which names the exact API call that rejected. Without it a user and a telemetry
 * event both see "InvalidArgument" and neither can act on it.
 */
interface OfficeExtensionErrorShape {
  readonly code?: unknown;
  readonly debugInfo?: {
    readonly errorLocation?: unknown;
    readonly message?: unknown;
  };
}

function readOfficeErrorDetail(error: unknown): string | null {
  if (typeof error !== "object" || error === null) return null;

  const { code, debugInfo } = error as OfficeExtensionErrorShape;
  const location = typeof debugInfo?.errorLocation === "string" ? debugInfo.errorLocation : null;
  const detail = typeof debugInfo?.message === "string" ? debugInfo.message : null;
  const codeText = typeof code === "string" ? code : null;

  const parts = [
    codeText,
    location ? `at ${location}` : null,
    // Only add debugInfo.message when it says something the code doesn't.
    detail && detail !== codeText ? `— ${detail}` : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(" ") : null;
}

/**
 * Centralised so every catch site in this feature extracts messages the same way.
 * Office.js failures are expanded to include their code and failing call site.
 */
export function getErrorMessage(error: unknown, fallback: string): string {
  const officeDetail = readOfficeErrorDetail(error);
  if (officeDetail) return officeDetail;
  return error instanceof Error && error.message ? error.message : fallback;
}

/** Reports a Slide Transform failure through the shared telemetry pipeline. */
export function logSlideTransformError(reason: string, message: string): void {
  logError(
    "error.connector.failure",
    { type: "SlideTransformError", scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: SLIDE_TRANSFORM_CONNECTOR, reason },
  );
}

/**
 * Builds a `.catch` handler that reports an otherwise-unhandled rejection.
 * The actions this wraps already catch their own errors and surface them via UI
 * state, so this only fires if a future change makes one throw before its own
 * try/catch runs — it exists so that case is reported rather than swallowed.
 */
export function reportSlideTransformFailure(reason: string): (error: unknown) => void {
  return (error: unknown) =>
    logSlideTransformError(reason, getErrorMessage(error, "Unexpected error"));
}
